import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, LinearProgress, Typography, Box, Fade } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TaskStatus } from '../../models/task.status';
import { NWClient } from '../../client/NWClient';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { clearTaskIds } from '../../slices/tasks/tasksSlice';
import { useTranslation } from 'react-i18next';

const ToastCard = styled(Card)(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(2),
  right: theme.spacing(2),
  width: 350,
  zIndex: theme.zIndex.snackbar,
  boxShadow: theme.shadows[8],
}));

interface UploadProgressProps {
  taskIds: string[];
  onComplete?: () => void;
}

const UploadProgress: React.FC<UploadProgressProps> = ({ taskIds }) => {
  const [taskStatuses, setTaskStatuses] = useState<Record<string, TaskStatus>>({});
  const [isVisible, setIsVisible] = useState(true);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const handleTaskStatusUpdate = useCallback((data: TaskStatus | TaskStatus[]) => {
    console.log('Received task update:', data);

    // Handle both single task and array of tasks
    const tasks = Array.isArray(data) ? data : [data];

    setTaskStatuses((prev) => {
      const updated = { ...prev };

      tasks.forEach((task) => {
        const taskId = task.task_id;

        if (!taskId) {
          return;
        }

        updated[taskId] = task;
      });

      return updated;
    });
  }, []);

  const handleError = useCallback((error: Event) => {
    console.error('Task status subscription error:', error);
  }, []);

  const handleUploadComplete = useCallback(() => {
    const docIds: number[] = [];

    console.log('Current taskStatuses:', taskStatuses);

    for (const [, taskStatusObject] of Object.entries(taskStatuses)) {
      if (taskStatusObject) {
        console.log(`Document ID being set: ${taskStatusObject.result}`);
        docIds.push(taskStatusObject.result);
      }
    }
    dispatch(clearTaskIds());
  }, [taskStatuses, dispatch]);

  useEffect(() => {
    if (taskIds.length === 0) return;

    console.log('Setting up EventSource for task IDs:', taskIds);
    setIsVisible(true);

    // Initialize task statuses with proper structure
    const initialStatuses: Record<string, TaskStatus> = {};
    taskIds.forEach((id) => {
      initialStatuses[id] = {
        task_id: id,
        result: 0,
        success: false, // Add status field if your TaskStatus model has it
      };
    });
    setTaskStatuses(initialStatuses);

    // Subscribe to task status updates using NWClient
    const unsubscribe = NWClient.subscribeToTaskStatus(
      taskIds,
      handleTaskStatusUpdate,
      handleError
    );

    return unsubscribe;
  }, [taskIds, handleTaskStatusUpdate, handleError]);

  // Calculate progress - use useMemo for better performance
  const { totalTasks, completedTasks, progressPercentage } = React.useMemo(() => {
    const total = taskIds.length;
    const completed = Object.values(taskStatuses).filter(
      (status) => status && status.success === true
    ).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    console.log(`Progress: ${completed}/${total} (${percentage}%)`);
    return {
      totalTasks: total,
      completedTasks: completed,
      progressPercentage: percentage,
    };
  }, [taskIds.length, taskStatuses]);

  // Check if all tasks are completed
  useEffect(() => {
    console.log(`Checking completion: ${completedTasks}/${totalTasks}`);

    if (totalTasks > 0 && completedTasks === totalTasks) {
      console.log('All tasks completed, hiding progress in 2 seconds');

      const timer = setTimeout(() => {
        console.log('Hiding progress component');
        setIsVisible(false);
        handleUploadComplete();
      }, 2000);

      // Cleanup timer if component unmounts
      return () => clearTimeout(timer);
    }
  }, [completedTasks, totalTasks, handleUploadComplete]);

  useEffect(() => {
    if (taskIds.length > 0) {
      setIsVisible(true);
    }
  }, [taskIds]);

  // Don't render if no tasks or not visible
  if (taskIds.length === 0 || !isVisible) {
    return null;
  }

  return (
    <Fade in={isVisible}>
      <ToastCard>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant='h6' component='div' gutterBottom>
              Processing Files
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {completedTasks === totalTasks
                ? t('messages.filesProcessedSuccessfully')
                : `${t('labels.processing')} ${Number(totalTasks) + 1} ${t('labels.file')}${
                    totalTasks !== 1 ? 's' : ''
                  }...`}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant='determinate'
              value={progressPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              {Number(completedTasks) + 1} of {Number(totalTasks) + 1} completed
            </Typography>
            <Typography variant='body2' fontWeight='medium'>
              {progressPercentage}%
            </Typography>
          </Box>
        </CardContent>
      </ToastCard>
    </Fade>
  );
};

export default UploadProgress;
